'use client'

import Image from 'next/image'
import { Play } from 'lucide-react'
import { cn } from '@/utils/styles/cn'
import { LAUNCH_VIDEO_SRC, LAUNCH_VIDEO_POSTER_SRC } from '@/features/landing-page/v3/launch-video'
import { LandingVideoThumbnail } from '@/features/landing-page/v3/landing-video-thumbnail'
import { useLandingMediaOverlay } from '@/features/landing-page/landing-media-overlay-context'

/**
 * Launch-video widget for the vision card. Two shapes:
 *   • `card` — desktop, bottom-left: the shared LandingVideoThumbnail (same styling as the testimonial
 *     "play video" widget) at 168×95 + "Watch video".
 *   • `pill` — mobile, inline beside the CTA: a compact ink (#131210) pill with a small thumbnail and
 *     "Watch video", matched to the CTA height.
 * Both open the live launch video in the shared media overlay. Client leaf so the section stays a
 * server component. The play affordance uses Greige/Ink #131210.
 */

const THUMB_URL = LAUNCH_VIDEO_POSTER_SRC

export function VisionFilmButton({ variant = 'card', className }: { variant?: 'card' | 'pill'; className?: string }) {
    const overlay = useLandingMediaOverlay()

    const open = () =>
        overlay.open({
            items: [{ type: 'video', src: LAUNCH_VIDEO_SRC, aspect: 'landscape', alt: 'Superscale launch video' }],
        })

    if (variant === 'pill') {
        return (
            <button
                type="button"
                onClick={open}
                aria-label="Watch the Superscale launch video"
                className={cn(
                    'group/film inline-flex h-8 shrink-0 items-center gap-2 rounded-[8px] bg-[#131210] py-1 pl-1 pr-3.5 text-white',
                    className
                )}
            >
                <span className="relative h-6 w-[38px] shrink-0 overflow-hidden rounded-[4px]">
                    <Image src={THUMB_URL} alt="" fill sizes="38px" className="object-cover" />
                    <span className="absolute inset-0 grid place-items-center bg-black/20">
                        <Play className="size-2.5 translate-x-[0.5px] fill-white text-white" />
                    </span>
                </span>
                <span className="text-[13px]! font-medium leading-none! tracking-[-0.01em]!">Watch video</span>
            </button>
        )
    }

    return (
        <LandingVideoThumbnail
            onClick={open}
            label="Watch video"
            ariaLabel="Watch the Superscale launch video"
            className={cn('h-[95px] w-[168px]', className)}
        >
            <Image src={THUMB_URL} alt="" fill sizes="168px" className="object-cover" />
        </LandingVideoThumbnail>
    )
}
